import {
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
  HostListener,
} from '@angular/core';
import * as BABYLON from 'babylonjs';
import { Firestore, collection, query, where, getDocs, getDoc, addDoc, doc, onSnapshot, DocumentReference, updateDoc, setDoc } from '@angular/fire/firestore';
import { Observable, takeUntil, switchMap, of, Subject } from 'rxjs';
import { User } from '@angular/fire/auth';
import { UserDocument } from '../../interfaces/user-document.interface';
import { AuthService } from '../../services/auth.service';
import { OpenAIService } from '../../services/openai.service';
import { SceneSetupService } from '../../services/babylon.service';
import { SearchResult, Plan } from '../../interfaces/searchResult.interface';
import { Timestamp } from 'firebase/firestore';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { DataService } from '../../services/data.service';
import { MissionComponent } from '../mission/mission.component';
import { LichSuDichVu } from '../lichSuDichVu/lichSuDichVu.component';
import { DanhSachDichVu } from '../danhSachDichVu/danhSachDichVu.component';
import { InviteFriendsComponent } from '../invite-friends/invite-friends.component';
import { GifterSceneComponent } from '../gifter-scene/gifter-scene.component';
import { GifterDetailComponent } from '../gifter-detail/gifter-detail.component';
import { CommonModule } from '@angular/common';

interface Problem {
  name: string;
  tag: string[];
}

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    MissionComponent,
    LichSuDichVu,
    DanhSachDichVu,
    InviteFriendsComponent,
    GifterSceneComponent,
    GifterDetailComponent,
    MissionComponent,
  ]
})
export class Home implements OnInit, OnDestroy {
  @ViewChild('renderCanvas', { static: true }) renderCanvas!: ElementRef<HTMLCanvasElement>;
  @ViewChild('userDetail', { static: true }) userDetailComponent: any;

  gameEngine!: BABYLON.Engine;
  gameScene!: BABYLON.Scene;
  canvas!: HTMLCanvasElement;

  private player!: BABYLON.Mesh;

  buttonFind: boolean = true;

  userData: UserDocument | null = null;

  private destroy$ = new Subject<void>();
  user$: Observable<User | null>;
  userDocRef: DocumentReference | null = null;
  csyt: boolean = false;
  payCenter: boolean = false;
  member: boolean = false;

  private isRecognizing = false;
  public transcriptText: string = '';
  allProblems: any[] = [];
  matchingProblems: Problem[] = [];

  private balls: BABYLON.Mesh[] = [];
  private pickBall: HTMLAudioElement;

  pickedProblems: Set<string> = new Set();
  public plansProblem: Plan[] = [];

  gameOverDiv: boolean = false;
  missionChild: boolean = false;

  outputPlanData: SearchResult | null = null;

  danhSachDv: boolean = false;
  lichSuDv: boolean = false;

  shouldClearBalls: boolean = false;

  showGifterScene: boolean = false;

  showGiftersList: boolean = false;
  selectedGifter: any = null;
  selectedGifterInfo: any = null;
  sortedGifters: any[] = [];

  constructor(
    private dataService: DataService,
    private firestore: Firestore,
    public authService: AuthService,
    private router: Router,
    private toastr: ToastrService,
    private ngZone: NgZone, // Thêm lại NgZone
    private sceneSetupService: SceneSetupService, // Thêm lại SceneSetupService
    private openAIService: OpenAIService // Thêm lại OpenAIService
  ) {
    this.user$ = this.authService.userSubject;
  }


  ngOnInit(): void {
    this.canvas = this.renderCanvas.nativeElement;
    this.initGameScene();

    // Lắng nghe sự kiện resize và fullscreen
    window.addEventListener('resize', this.onResize);
    document.addEventListener('fullscreenchange', this.onResize);
    document.addEventListener('webkitfullscreenchange', this.onResize);
    document.addEventListener('mozfullscreenchange', this.onResize);
    document.addEventListener('msfullscreenchange', this.onResize);
  }

  ngAfterViewInit(): void {
    this.onResize(); // Đảm bảo canvas và engine được resize ngay sau khi DOM render

    // Lắng nghe dữ liệu người dùng trước, sau đó mới tải nhiệm vụ
    this.listenUserData();

  }

  private initGameScene(): void {
    const { engine, scene } = this.sceneSetupService.createGameScene(this.canvas);
    this.gameEngine = engine;
    this.gameScene = scene;

    this.sceneSetupService.setupBackground(scene);
    this.sceneSetupService.createCamera(scene);

    this.gameEngine.runRenderLoop(() => {
      this.gameScene.render();
    });

    // Tạo player với callback
    this.player = this.sceneSetupService.createHomePlayer(scene, async () => {
      if (this.allProblems.length === 0) {
        await this.fetchAllProblems();
      }
      this.stop(scene);
    });

    this.preloadPickBall();
  }

  private onResize = (): void => {
    if (this.gameEngine) {
      this.gameEngine.resize(); // Resize engine
    }
    this.adjustUI(); // Điều chỉnh UI nếu cần
  };

  private adjustUI() {
    const buttons = document.querySelectorAll('button');

    buttons.forEach((button) => {
      (button as HTMLElement).style.width = '100%';
      (button as HTMLElement).style.bottom = '10px'; // Điều chỉnh khoảng cách từ đáy
    });
  }

  private listenUserData(): void {
    // Combine cả user authentication và userData
    this.authService.userSubject.pipe(
      takeUntil(this.destroy$),
      switchMap(user => {
        if (user) {
          this.userDocRef = doc(this.firestore, `User/${user.uid}`);
          return this.dataService.userData$;
        } else {
          this.userDocRef = null;
          return of(null);
        }
      })
    ).subscribe((userData) => {
      this.userData = userData;
      if (this.userData?.role?.includes('pharmacy')) {
        this.router.navigate(['nhaThuocTong']);
      } else if (this.userData?.role?.includes('labhouse')) {
        this.router.navigate(['labhouse']);
      }
      this.csyt = this.userData?.role?.includes('csyt') || false;
      this.payCenter = this.userData?.role?.includes('paymentcenter') || false;
      this.member = (this.userData?.diamonds || 0) >= 5000;
      this.missionChild = true;
    });
  }

  private async fetchAllProblems(): Promise<void> {
    // Kiểm tra nếu dữ liệu đã được tải
    if (this.allProblems.length > 0) {
      console.log('Dữ liệu đã được tải trước đó:', this.allProblems);
      return;
    }

    try {
      const problemsRef = collection(this.firestore, 'problems');
      const querySnapshot = await getDocs(problemsRef);
      this.allProblems = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      console.log('All problems loaded:', this.allProblems);
    } catch (error) {
      console.error('Error fetching problems:', error);
    }
  }

  toggleLichSuDv(): void {
    try {
      console.log('Before: lichSuDv =', this.lichSuDv);
      this.missionChild = false;
      this.danhSachDv = false;
      this.lichSuDv = !this.lichSuDv;
      console.log('After: lichSuDv =', this.lichSuDv);
      this.gameOverDiv = false;

      console.log('userDetailComponent:', this.userDetailComponent);
      if (this.userDetailComponent) {
        this.userDetailComponent.showPlanDocList = true;
        console.log('Set showPlanDocList to true');
      } else {
        console.log('userDetailComponent is not available');
      }
    } catch (error) {
      console.error('Error in action:', error);
    }
  }

  toggleDanhSachDv(): void {
    this.missionChild = false;
    this.lichSuDv = false;
    this.danhSachDv = !this.danhSachDv;
    this.gameOverDiv = false;
    // Xóa bóng cũ với hiệu ứng
    this.balls.forEach((b) => this.sceneSetupService.fadeOutAndDispose(b));
    this.balls = [];
    this.plansProblem = [];
    this.pickedProblems.clear();
    this.outputPlanData = null;

    // Clear balls in child component directly
    if (this.userDetailComponent) {
      this.userDetailComponent.clearBalls();
    }
  }

  navigatePaymentCenter() {
    this.router.navigate(['payment-center']);
  }

  navigateToQlyHoSo(): void {
    this.router.navigate(['quanLyHoSo']);
  }

  navigateToQlyThanhVien(): void {
    // Navigate to the quanLyThanhVien component
    this.router.navigate(['quanLyThanhVien']);
  }

  private async disposeGame(): Promise<void> {
    this.gameEngine.dispose();
    this.gameScene.dispose();
    this.matchingProblems = [];
    this.balls = [];
    this.pickedProblems.clear();
    this.player.dispose();
  }

  async stop(scene: BABYLON.Scene) {
    this.gameOverDiv = false;
    this.ngZone.runOutsideAngular(() => {
      // Xóa bóng cũ với hiệu ứng
      this.balls.forEach((b) => this.sceneSetupService.fadeOutAndDispose(b));
      this.balls = [];
      this.plansProblem = [];
      this.pickedProblems.clear();
      this.outputPlanData = null;
    });

    // Hiển thị prompt để người dùng nhập thêm thông tin
    this.transcriptText = window.prompt('Nhập chữ hoặc dùng micro bàn phím:', '');

    const words = this.transcriptText?.toLowerCase() || '';
    const matchingProblems = await this.allProblems.filter(
      (problem) =>
        Array.isArray(problem.tag) &&
        problem.tag.some((tag) => words.includes(tag.toLowerCase()))
    );

    this.matchingProblems = Array.from(
      new Map(matchingProblems.map((problem) => [problem.id, problem])).values()
    );

    if (this.matchingProblems.length === 0) {
      this.toastr.error('Hãy nhập thêm thông tin!', 'Không vấn đề phù hợp:');
      return;
    }

    this.createBalls(scene);
    this.toastr.info('Xanh lá cây là bình thường!', 'Đánh giá rối loạn:');
  }

  private createBalls(scene: BABYLON.Scene): void {
    const availableProblems = this.matchingProblems.filter(
      (problem) => !this.pickedProblems.has(problem.name)
    );

    if (availableProblems.length > 0) {
      const randomProblem = availableProblems[Math.floor(Math.random() * availableProblems.length)];

      // Xử lý click bóng
      const onPickCallback = (option: number) => {
        this.ngZone.run(() => {
          this.pickBall.currentTime = 0;
          this.pickBall.play();

          this.plansProblem = this.plansProblem.filter(
            (plan) => plan.name !== randomProblem.name
          );
          this.plansProblem.push({
            name: randomProblem.name,
            option,
          });

          this.pickedProblems.add(randomProblem.name);

          // Xóa bóng cũ với hiệu ứng
          this.balls.forEach((b) => this.sceneSetupService.fadeOutAndDispose(b));
          this.balls = [];

          if (this.pickedProblems.size < this.matchingProblems.length) {
            this.createBalls(scene);
          } else {
            this.endGame();
          }
        });
      };

      // Gọi service tạo bóng
      this.balls = this.sceneSetupService.createBalls(
        scene,
        randomProblem,
        this.pickedProblems,
        onPickCallback
      );
    } else if (this.pickedProblems.size > 0) {
      this.endGame();
    }
  }

  private endGame() {
    this.balls.forEach((ball) => ball.dispose());
    this.balls = [];
    this.gameOverDiv = true;
  }

  public replay() {
    this.gameOverDiv = false;

    this.ngZone.runOutsideAngular(() => {
      this.balls = [];
      this.plansProblem = [];
      this.pickedProblems.clear();
      this.outputPlanData = null;
    });

    this.buttonFind = true;
    this.createBalls(this.gameScene);
  }

  public async getGptGuide() {

    if (!this.userData) {
      this.toastr.error('Bạn cần đăng nhập để sử dụng tính năng này!', 'Lỗi!');
      return;
    }

    const medRecords = `
      Diễn biến: ${this.transcriptText || 'Chưa có'}.
      Trong đó, mức độ một số triệu chứng là: ${this.plansProblem || 'Chưa có'}.
      Lưu ý: Mức độ các triệu chứng được quy định bán định lượng như sau: 1: Hoàn toàn bình thường. 2: Mức độ nhẹ, cần theo dõi. 3: Mức độ vừa, có thể trì hoãn can thiệp. 4: Mức độ nặng, cần can thiệp ngay. 5: Mức độ rất nặng, cần can thiệp khẩn cấp.
    `;

    if (!medRecords.trim()) {
      this.toastr.error('Không có dữ liệu hợp lệ để gửi!', 'Lỗi!');
      return;
    }

    this.buttonFind = false;

    const coins = 1;

    const result = await this.openAIService.processOpenAIResponse(
      medRecords,
      this.userDocRef,
      this.userData,
      coins,
      true
    );

    if (result) {
      this.outputPlanData = result; // Gán dữ liệu sau khi xử lý
    } else {
      this.toastr.error('Không thể lấy dữ liệu!');
    }

  }

  callDoctor(): void {
    window.location.href = 'tel:0981919115';
  }

  private preloadPickBall() {
    const pickBall = new Audio();
    pickBall.src = '/assets/audio/pickCoin.mp3';
    pickBall.load();
    this.pickBall = pickBall;
  }

  ngOnDestroy(): void {
    window.removeEventListener('resize', this.onResize);
    document.removeEventListener('fullscreenchange', this.onResize);
    document.removeEventListener('webkitfullscreenchange', this.onResize);
    document.removeEventListener('mozfullscreenchange', this.onResize);
    document.removeEventListener('msfullscreenchange', this.onResize);

    this.gameEngine.dispose();

    this.destroy$.next();
    this.destroy$.complete();
    this.showGiftersList = false;
  }

  public navigateToFarm(): void {
    // Navigate to the farm component
    this.router.navigate(['farm']);
  }

  requestEmergencyService(): void {
    if (!this.member) {
      alert('Bạn cần là thành viên thân thiết để sử dụng dịch vụ khẩn cấp.');
      return;
    }

    const confirmRequest = confirm('Bạn có chắc chắn muốn gửi yêu cầu dịch vụ khẩn cấp?');
    if (!confirmRequest) {
      return; // Người dùng không đồng ý, thoát khỏi hàm
    }

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const latitude = position.coords.latitude;
          const longitude = position.coords.longitude;
          this.savePlans(latitude, longitude);
          // Gửi yêu cầu dịch vụ khẩn cấp với vị trí
          console.log('Yêu cầu dịch vụ khẩn cấp với vị trí:', { latitude, longitude });
          alert(`Dịch vụ khẩn cấp đã được yêu cầu tại vị trí: (${latitude}, ${longitude})`);
          // Thêm logic gửi yêu cầu đến server tại đây
        },
        (error) => {
          console.error('Không thể lấy vị trí:', error);
          alert('Không thể lấy vị trí của bạn. Vui lòng kiểm tra cài đặt vị trí.');
        }
      );
    } else {
      alert('Trình duyệt của bạn không hỗ trợ lấy vị trí.');
    }
  }

  async savePlans(latitude: number, longitude: number) {
    try {
      const planData: SearchResult = {
        clientEmail: this.userData.email,
        clientUid: this.userDocRef.id,
        createdAt: Timestamp.fromDate(new Date()),
        providerEmail: 'thienbinhcareplus@gmail.com',
        guide: null,
        history: ['Yêu cầu khẩn cấp'],
        alarm: true,
        position: { lat: latitude, lng: longitude },
      };

      // Lưu vào subcollection plans của provider (ví dụ pkthienbinh)
      const providerUid = 'KZQol09qHyNrpJei1mcAgfg5riW2'; // hoặc lấy từ nơi khác nếu cần
      const providerDocRef = doc(this.firestore, `User/${providerUid}`);
      const plansCollectionRef = collection(providerDocRef, 'plans');
      const docRef = await addDoc(plansCollectionRef, planData);

      this.toastr.success('Chúng tôi sẽ liên hệ lại!', 'Yêu cầu khẩn cấp!');
    } catch (error) {
      console.error('Error adding plan: ', error);
    }
  }

  toggleGiftersList(): void {
    if (!this.userData) {
      this.authService.signOut();
      return;
    }

    this.showGiftersList = !this.showGiftersList;
    if (this.showGiftersList) {
      this.loadGifters();
    }
  }

  loadGifters(): void {
    if (!this.userData?.gifters) {
      this.sortedGifters = [];
      return;
    }

    // Sắp xếp theo thời gian mới nhất (lastSent)
    this.sortedGifters = [...this.userData.gifters].sort((a, b) => {
      const timeA = a.lastSent ? new Date(a.lastSent).getTime() : 0;
      const timeB = b.lastSent ? new Date(b.lastSent).getTime() : 0;
      return timeB - timeA; // Mới nhất ở trên
    });
  }

  getDisplayName(email: string): string {
    if (!email) return 'Không rõ';
    return email.split('@')[0];
  }

  async showGifterDetail(gifter: any): Promise<void> {
    this.selectedGifter = gifter;
    this.showGiftersList = false; // Đóng dropdown khi mở detail

    // Đánh dấu đã xem
    if (!gifter.seen) {
      await this.markGifterAsSeen(gifter);
    }

    // Tải thông tin chi tiết của gifter bằng userDocId
    await this.loadGifterInfo(gifter.userDocId);
  }

  async markGifterAsSeen(gifter: any): Promise<void> {
    if (!this.userDocRef) return;

    try {
      const updatedGifters = this.userData.gifters.map((g: any) =>
        g.email === gifter.email ? { ...g, seen: true } : g
      );

      await updateDoc(this.userDocRef, { gifters: updatedGifters });

      // Cập nhật local data
      gifter.seen = true;
      this.userData.gifters = updatedGifters;
    } catch (error) {
      console.error('Error marking gifter as seen:', error);
    }
  }

  async loadGifterInfo(userDocId: string): Promise<void> {
    try {
      // Truy xuất trực tiếp bằng ID - nhanh hơn nhiều
      const userDocRef = doc(this.firestore, `User/${userDocId}`);
      const userSnapshot = await getDoc(userDocRef);

      if (userSnapshot.exists()) {
        this.selectedGifterInfo = userSnapshot.data();
      } else {
        this.selectedGifterInfo = { displayName: 'Không tìm thấy', organize: 'Không có' };
      }
    } catch (error) {
      console.error('Error loading gifter info:', error);
      this.selectedGifterInfo = { displayName: 'Lỗi tải dữ liệu', organize: 'Không có' };
    }
  }

  closeGifterDetail(): void {
    this.selectedGifter = null;
    this.selectedGifterInfo = null;
  }

  formatTime(timeString: string): string {
    if (!timeString) return '';

    const date = new Date(timeString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Vừa xong';
    if (diffMins < 60) return `${diffMins}p`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 7) return `${diffDays}d`;

    return date.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit' });
  }

  signOut(): void {
    this.showGiftersList = false;
    this.authService.signOut();
  }

  // Thêm event listener để đóng dropdown khi click bên ngoài
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    const diamondsElement = document.getElementById('diamonds');
    const dropdownElement = document.querySelector('.gifters-dropdown');

    // Nếu click không phải trên nút diamonds hoặc dropdown thì đóng menu
    if (this.showGiftersList &&
      diamondsElement &&
      !diamondsElement.contains(target) &&
      dropdownElement &&
      !dropdownElement.contains(target)) {
      this.showGiftersList = false;
    }
  }

  openGifterScene() {
    if (this.gameEngine) {
      //Đóng các div khác
      this.showGiftersList = false;
      this.gameOverDiv = false;
      this.missionChild = false;
      this.danhSachDv = false;
      this.lichSuDv = false;
      this.showGifterScene = true;
    }
  }

  public returnToHomeView(): void {
    this.ngZone.run(() => {
      console.log('returnToHomeView started');

      // Reset tất cả các flags về trạng thái home
      this.gameOverDiv = false;
      this.missionChild = false;
      this.danhSachDv = false;
      this.lichSuDv = false;

      if (this.gameScene) {
        console.log('Setting up home scene');

        // Tạo camera mới
        this.gameScene.activeCamera = this.sceneSetupService.createCamera(this.gameScene);

        // Setup background
        this.sceneSetupService.setupBackground(this.gameScene);

        // ✨ Force trigger resize event để refresh background
        setTimeout(() => {
          window.dispatchEvent(new Event('resize'));
        }, 100);

        if (!this.player) {
          this.player = this.sceneSetupService.createHomePlayer(this.gameScene, async () => {
            this.missionChild = false;
            this.danhSachDv = false;
            this.lichSuDv = false;
            this.gameOverDiv = false;
            if (this.allProblems.length === 0) {
              await this.fetchAllProblems();
            }
            this.stop(this.gameScene);
          });

          console.log('Home scene setup completed');
        }
      }
      // Ẩn Gifter scene để quay lại view cha
      this.showGifterScene = false;

      console.log('returnToHomeView completed');
    });
  }

  // Thêm method để clear scene
  private clearScene(): void {
    if (!this.gameScene) return;

    console.log('Clearing scene');

    // Xóa tất cả background layers cũ
    this.gameScene.layers.forEach(layer => {
      if (layer.isBackground) {
        layer.dispose();
      }
    });

    // Xóa tất cả meshes cũ (trừ những mesh cần thiết)
    const meshesToRemove = this.gameScene.meshes.slice();
    meshesToRemove.forEach(mesh => {
      mesh.dispose();
    });

    // Xóa materials cũ
    const materialsToRemove = this.gameScene.materials.slice();
    materialsToRemove.forEach(material => {
      material.dispose();
    });

    // Xóa textures cũ
    const texturesToRemove = this.gameScene.textures.slice();
    texturesToRemove.forEach(texture => {
      texture.dispose();
    });

    console.log('Scene cleared');
  }

}
